<script setup lang="ts">
import maplibregl, { Popup } from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, onUnmounted, ref, shallowRef, watch, type Ref } from 'vue'

import { isTouchDevice } from '@/services/uiService'
import { getWaypointIcon } from '@/services/waypointIconService'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'
import { hideMapHoverPopup, showMapHoverPopup } from '@/utils/mapHoverPopup'
import { createMapMarker, createMapMarkerElement } from '@/utils/mapMarkerElement'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import { buildWorkspaceWaypointHoverPopupHtml } from '@/utils/tourHoverPopup'

defineOptions({ name: 'WorkspaceWaypointDotLayer' })

const props = defineProps<{
  tours: Tour[]
}>()

const emit = defineEmits<{
  'waypoint-edit': [tourId: string, waypointIndex: number]
}>()

const uiStore = useUiStore()
const workspaceStore = useWorkspaceStore()
const mapInstance = inject<Ref<maplibregl.Map | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

const sourceId = 'workspace-waypoint-dot-source'
const layerId = 'workspace-waypoint-dot-layer'

const popup = shallowRef<Popup | null>(null)
const currentPopupId = ref<string | null>(null)

// Progressive disclosure: zoomed out the collection shows compact dots (avoids clutter across many
// routes); zoomed in close they upgrade to the full category icon marker — identical to the in-tour
// rendering — so a user examining an area can tell what each waypoint is, on desktop and mobile
// alike. Single clean switch at this zoom (street/block level). See WaypointMarkerLayer for the
// in-tour markers this mirrors.
const WAYPOINT_DETAIL_MIN_ZOOM = 15

type SimpleMarker = { remove: () => void }
const detailMarkers = ref<SimpleMarker[]>([])
const detailMarkerElements = ref<Map<string, HTMLDivElement>>(new Map())
const isDetailZoom = ref(false)

const computeDetailZoom = () => {
  const map = mapInstance.value
  return !!map && map.getZoom() >= WAYPOINT_DETAIL_MIN_ZOOM
}

const { hoveredWaypointId, isTrackDrawingActive } = storeToRefs(uiStore)
const { selectedTourId, activelyHoveredTourId } = storeToRefs(workspaceStore)

const shouldRenderWaypoints = computed(
  () =>
    uiStore.areWaypointsVisible &&
    !uiStore.areMapElementsForceHidden &&
    !uiStore.isInWorkspacesOverview &&
    !uiStore.isAllActivitiesView,
)

const getVisibleWaypointFeatures = (): GeoJSON.Feature[] =>
  props.tours.flatMap((tour) => {
    if (
      tour.clientId === selectedTourId.value ||
      tour.visible === false ||
      tour._waypointsVisible === false
    ) {
      return []
    }
    const color = resolveTourAccentColor(tour)
    return tour.waypoints
      .map((waypoint, waypointIndex) => ({
        type: 'Feature' as const,
        properties: {
          id: `${tour.clientId}_${waypointIndex}`,
          tourId: tour.clientId,
          waypointIndex,
          waypointName: waypoint.name || 'Waypoint',
          waypointType: waypoint.type || '',
          color,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [waypoint.lon, waypoint.lat],
        },
      }))
      .filter(
        (feature) =>
          typeof feature.geometry.coordinates[0] === 'number' &&
          typeof feature.geometry.coordinates[1] === 'number' &&
          !uiStore.isWaypointHidden(feature.properties.id as string),
      )
  })

const getGeoJsonData = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: shouldRenderWaypoints.value ? getVisibleWaypointFeatures() : [],
})

const buildRadiusExpression = (): maplibregl.ExpressionSpecification => [
  'case',
  ['==', ['get', 'tourId'], activelyHoveredTourId.value ?? '__no-hover__'],
  6,
  4,
]

const buildStrokeWidthExpression = (): maplibregl.ExpressionSpecification => [
  'case',
  ['==', ['get', 'tourId'], activelyHoveredTourId.value ?? '__no-hover__'],
  6,
  4,
]

const clearWaypointHover = () => {
  if (hoveredWaypointId.value && !hoveredWaypointId.value.startsWith('poi_')) {
    uiStore.setHoveredWaypoint(null)
  }
  workspaceStore.setActivelyHoveredTourId(null)
  workspaceStore.setHoveredTourId(workspaceStore.selectedTourId ?? null)
}

const applyWaypointHover = (tourId: string, waypointId: string) => {
  uiStore.setHoveredWaypoint(waypointId)
  workspaceStore.setActivelyHoveredTourId(tourId)
  if (workspaceStore.hoveredTourId !== tourId) {
    workspaceStore.setHoveredTourId(tourId)
  }
}

const syncSourceData = () => {
  const source = mapInstance.value?.getSource(sourceId) as maplibregl.GeoJSONSource | undefined
  if (source) source.setData(getGeoJsonData())
}

const updateHoverStyle = () => {
  const map = mapInstance.value
  if (!map?.getLayer(layerId)) return

  map.setPaintProperty(layerId, 'circle-radius-transition', { duration: 0, delay: 0 })
  map.setPaintProperty(layerId, 'circle-stroke-width-transition', { duration: 0, delay: 0 })
  map.setPaintProperty(layerId, 'circle-radius', buildRadiusExpression())
  map.setPaintProperty(layerId, 'circle-stroke-width', buildStrokeWidthExpression())
}

const removeDetailMarkers = () => {
  detailMarkers.value.forEach((marker) => marker.remove())
  detailMarkers.value = []
  detailMarkerElements.value.clear()
}

const updateDetailMarkerHoverStyle = () => {
  detailMarkerElements.value.forEach((markerEl, waypointId) => {
    const isHovered = hoveredWaypointId.value === waypointId
    markerEl.style.transform = isHovered ? 'scale(1.18)' : 'scale(1)'
    markerEl.style.transition = 'none'
  })
}

// Render the full icon markers for every non-selected, visible waypoint (the selected tour is owned
// by WaypointMarkerLayer). Only active when zoomed in past WAYPOINT_DETAIL_MIN_ZOOM.
const addDetailMarkers = () => {
  const map = mapInstance.value
  if (!map) return
  removeDetailMarkers()
  if (!shouldRenderWaypoints.value || !isDetailZoom.value) return

  props.tours.forEach((tour) => {
    if (
      tour.clientId === selectedTourId.value ||
      tour.visible === false ||
      tour._waypointsVisible === false
    ) {
      return
    }
    const color = resolveTourAccentColor(tour)
    tour.waypoints.forEach((waypoint, waypointIndex) => {
      if (!Number.isFinite(waypoint.lon) || !Number.isFinite(waypoint.lat)) return
      const waypointId = `${tour.clientId}_${waypointIndex}`
      if (uiStore.isWaypointHidden(waypointId)) return

      const { element, touchArea, markerEl } = createMapMarkerElement(
        getWaypointIcon(waypoint.type || ''),
        color,
        'waypoint-marker-container',
      )
      detailMarkerElements.value.set(waypointId, markerEl)

      touchArea.addEventListener('click', (event) => {
        if (isTrackDrawingActive.value) return
        event.preventDefault()
        event.stopPropagation()
        emit('waypoint-edit', tour.clientId, waypointIndex)
      })
      touchArea.addEventListener('touchend', (event) => {
        if (isTrackDrawingActive.value) return
        event.preventDefault()
        event.stopPropagation()
        emit('waypoint-edit', tour.clientId, waypointIndex)
      })
      touchArea.addEventListener('mouseenter', () => {
        if (isTrackDrawingActive.value || isTouchDevice()) return
        applyWaypointHover(tour.clientId, waypointId)
      })
      touchArea.addEventListener('mouseleave', () => {
        if (isTrackDrawingActive.value) return
        clearWaypointHover()
      })

      const marker = createMapMarker({ element, anchor: 'center' })
        .setLngLat([waypoint.lon, waypoint.lat])
        .addTo(map)
      detailMarkers.value.push(marker as SimpleMarker)
    })
  })

  updateDetailMarkerHoverStyle()
}

// Reconcile the two presentations: compact dots when zoomed out, full icon markers when zoomed in.
// They are mutually exclusive so the same waypoint never renders twice.
const updateLayerVisibility = () => {
  const map = mapInstance.value
  if (!map?.getLayer(layerId)) return

  const showDots = shouldRenderWaypoints.value && !isDetailZoom.value
  map.setLayoutProperty(layerId, 'visibility', showDots ? 'visible' : 'none')

  if (shouldRenderWaypoints.value && isDetailZoom.value) {
    addDetailMarkers()
  } else {
    removeDetailMarkers()
  }

  if (!shouldRenderWaypoints.value) hidePopup()
}

const removeLayers = () => {
  const map = mapInstance.value
  if (!map) return
  if (map.getLayer(layerId)) map.removeLayer(layerId)
  if (map.getSource(sourceId)) map.removeSource(sourceId)
}

const showPopup = (waypointId: string) => {
  const map = mapInstance.value
  if (!map || currentPopupId.value === waypointId) return

  currentPopupId.value = waypointId
  const tourId = waypointId.substring(0, waypointId.lastIndexOf('_'))
  const waypointIndex = Number(waypointId.split('_').at(-1) ?? -1)
  const tour = props.tours.find((candidate) => candidate.clientId === tourId)
  const waypoint =
    tour && Number.isInteger(waypointIndex) && waypointIndex >= 0
      ? tour.waypoints[waypointIndex]
      : null
  if (!tour || !waypoint || !Number.isFinite(waypoint.lon) || !Number.isFinite(waypoint.lat)) return

  popup.value = showMapHoverPopup({
    map,
    popup: popup.value,
    lngLat: [waypoint.lon, waypoint.lat],
    html: buildWorkspaceWaypointHoverPopupHtml(tour, waypoint),
    className: 'waypoint-popup',
    offset: 15,
  })
}

const hidePopup = () => {
  popup.value = hideMapHoverPopup(popup.value)
  currentPopupId.value = null
}

const eventHandlers = {
  mouseenter: null as ((e: maplibregl.MapLayerMouseEvent) => void) | null,
  mousemove: null as ((e: maplibregl.MapLayerMouseEvent) => void) | null,
  mouseleave: null as (() => void) | null,
  click: null as ((e: maplibregl.MapLayerMouseEvent) => void) | null,
  zoom: null as (() => void) | null,
}

const removeEventHandlers = () => {
  const map = mapInstance.value
  if (!map) return
  if (eventHandlers.mouseenter) map.off('mouseenter', layerId, eventHandlers.mouseenter)
  if (eventHandlers.mousemove) map.off('mousemove', layerId, eventHandlers.mousemove)
  if (eventHandlers.mouseleave) map.off('mouseleave', layerId, eventHandlers.mouseleave)
  if (eventHandlers.click) map.off('click', layerId, eventHandlers.click)
  if (eventHandlers.zoom) map.off('zoom', eventHandlers.zoom)
}

const initializeLayers = () => {
  const map = mapInstance.value
  if (!map || !mapLoaded.value) return

  removeEventHandlers()
  removeLayers()

  map.addSource(sourceId, {
    type: 'geojson',
    data: getGeoJsonData(),
  })

  map.addLayer({
    id: layerId,
    type: 'circle',
    source: sourceId,
    layout: {
      visibility: shouldRenderWaypoints.value ? 'visible' : 'none',
    },
    paint: {
      'circle-color': '#FFFFFF',
      'circle-radius': buildRadiusExpression(),
      'circle-radius-transition': { duration: 0, delay: 0 },
      'circle-stroke-width': buildStrokeWidthExpression(),
      'circle-stroke-width-transition': { duration: 0, delay: 0 },
      'circle-stroke-color': ['get', 'color'],
      'circle-opacity': 1,
      'circle-stroke-opacity': 1,
    },
  })

  const applyHoverFromEvent = (event: maplibregl.MapLayerMouseEvent) => {
    const feature = map.queryRenderedFeatures(event.point, { layers: [layerId] })[0]
    const tourId = feature?.properties?.tourId as string | undefined
    const waypointIndex = Number(feature?.properties?.waypointIndex ?? -1)
    if (!tourId || waypointIndex < 0) return
    const waypointId = `${tourId}_${waypointIndex}`
    applyWaypointHover(tourId, waypointId)
  }

  eventHandlers.mouseenter = (event) => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = 'pointer'
    if (isTouchDevice()) return
    applyHoverFromEvent(event)
  }
  eventHandlers.mousemove = (event) => {
    if (isTrackDrawingActive.value || isTouchDevice()) return
    applyHoverFromEvent(event)
  }
  eventHandlers.mouseleave = () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = ''
    clearWaypointHover()
  }
  eventHandlers.click = (event) => {
    if (isTrackDrawingActive.value) return
    const feature = map.queryRenderedFeatures(event.point, { layers: [layerId] })[0]
    const tourId = feature?.properties?.tourId as string | undefined
    const waypointIndex = Number(feature?.properties?.waypointIndex ?? -1)
    if (!tourId || waypointIndex < 0) return
    emit('waypoint-edit', tourId, waypointIndex)
  }

  map.on('mouseenter', layerId, eventHandlers.mouseenter)
  map.on('mousemove', layerId, eventHandlers.mousemove)
  map.on('mouseleave', layerId, eventHandlers.mouseleave)
  map.on('click', layerId, eventHandlers.click)

  // Switch between dots and full icon markers as the user crosses the detail zoom threshold.
  // Guarded so we only re-render on an actual state change, not on every zoom frame.
  eventHandlers.zoom = () => {
    const next = computeDetailZoom()
    if (next !== isDetailZoom.value) {
      isDetailZoom.value = next
      updateLayerVisibility()
    }
  }
  map.on('zoom', eventHandlers.zoom)

  isDetailZoom.value = computeDetailZoom()
  updateLayerVisibility()
}

onMounted(() => {
  watch(
    mapLoaded,
    (loaded) => {
      if (loaded) initializeLayers()
    },
    { immediate: true },
  )
})

watch([hoveredWaypointId, activelyHoveredTourId], ([newId], [oldId]) => {
  updateHoverStyle()
  updateDetailMarkerHoverStyle()
  if (oldId) hidePopup()
  if (
    newId &&
    shouldRenderWaypoints.value &&
    !newId.startsWith('poi_') &&
    newId.substring(0, newId.lastIndexOf('_')) !== selectedTourId.value
  ) {
    showPopup(newId)
  }
})

watch(
  () => uiStore.hiddenWaypointIds,
  () => {
    if (currentPopupId.value && uiStore.isWaypointHidden(currentPopupId.value)) {
      clearWaypointHover()
      hidePopup()
    }
    if (!mapLoaded.value) return
    syncSourceData()
    updateLayerVisibility()
  },
  { deep: true },
)

watch(
  () =>
    [
      props.tours,
      selectedTourId.value,
      uiStore.areWaypointsVisible,
      uiStore.areMapElementsForceHidden,
      uiStore.isInWorkspacesOverview,
      uiStore.hiddenWaypointIds,
    ] as const,
  () => {
    if (!mapLoaded.value) return
    syncSourceData()
    updateLayerVisibility()
  },
  { deep: true },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (!mapLoaded.value) return
    initializeLayers()
  },
)

onUnmounted(() => {
  clearWaypointHover()
  hidePopup()
  removeEventHandlers()
  removeDetailMarkers()
  removeLayers()
})
</script>

<template>
  <div class="hidden"></div>
</template>
